import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { galleryData } from '../data/Gallery';
import { Button } from '../utils/Button';
import { FaUpload, FaTrash, FaEye, FaSpinner, FaCloudUploadAlt, FaTimes } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { getImageUrl } from '../utils/imageOptimization';
import { 
  uploadImageToCloudinary, 
  deleteImageFromCloudinary, 
  validateImageFile, 
  getCategoryOptions,
  testUploadPreset
} from '../services/galleryService';

const GalleryManagement = () => {
  const { isAdmin } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState('showcase');
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadedCount, setUploadedCount] = useState(0);



  useEffect(() => {
    if (!isAdmin) {
      window.location.href = '/login';
    }
  }, [isAdmin]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      selectedFiles.forEach(fileData => {
        URL.revokeObjectURL(fileData.preview);
      });
    };
  }, [selectedFiles]);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    handleFiles(files);
  };

  const handleFiles = (files) => {
    const validFiles = [];
    const errors = [];

    files.forEach(file => {
      const validation = validateImageFile(file);
      if (validation.isValid) {
        validFiles.push({
          file,
          id: Math.random().toString(36).substr(2, 9),
          preview: URL.createObjectURL(file),
          status: 'pending' // pending, uploading, success, error
        });
      } else {
        errors.push(`${file.name}: ${validation.errors.join(', ')}`);
      }
    });

    if (errors.length > 0) {
      setError(errors.join('; '));
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
      setError('');
    }
  };

  const removeFile = (fileId) => {
    setSelectedFiles(prev => {
      const fileToRemove = prev.find(f => f.id === fileId);
      if (fileToRemove) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter(f => f.id !== fileId);
    });
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const files = Array.from(e.dataTransfer.files);
      handleFiles(files);
    }
  }, []);

  const uploadToCloudinary = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select files to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError('');
    setUploadedCount(0);

    const totalFiles = selectedFiles.length;
    let successCount = 0;
    let errorCount = 0;

    // Update all files to uploading status
    setSelectedFiles(prev => prev.map(f => ({ ...f, status: 'uploading' })));

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const fileData = selectedFiles[i];
        
        // Update progress
        const progress = ((i + 1) / totalFiles) * 100;
        setUploadProgress(progress);

        try {
          const result = await uploadImageToCloudinary(fileData.file, selectedCategory);
          
          if (result.success) {
            // Update file status to success
            setSelectedFiles(prev => prev.map(f => 
              f.id === fileData.id ? { ...f, status: 'success' } : f
            ));
            successCount++;
          } else {
            // Update file status to error
            setSelectedFiles(prev => prev.map(f => 
              f.id === fileData.id ? { ...f, status: 'error', error: result.error } : f
            ));
            errorCount++;
          }
        } catch (error) {
          // Update file status to error
          setSelectedFiles(prev => prev.map(f => 
            f.id === fileData.id ? { ...f, status: 'error', error: error.message } : f
          ));
          errorCount++;
        }

        setUploadedCount(i + 1);
      }

             // Show final results
       if (successCount > 0) {
         setSuccess(`Successfully uploaded ${successCount} image${successCount > 1 ? 's' : ''}! Images will appear in the gallery shortly.`);
         setTimeout(() => setSuccess(''), 5000);
       }

      if (errorCount > 0) {
        setError(`${errorCount} image${errorCount > 1 ? 's' : ''} failed to upload. Check individual files for details.`);
      }

      // Clear successful uploads after 3 seconds
      setTimeout(() => {
        setSelectedFiles(prev => prev.filter(f => f.status !== 'success'));
        setUploadProgress(0);
        setUploadedCount(0);
      }, 3000);

    } catch (error) {
      console.error('Upload error:', error);
      setError('Failed to upload images. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const deleteImage = async (imageUrl, imageIndex) => {
    if (!window.confirm('Are you sure you want to delete this image? This action cannot be undone.')) {
      return;
    }

    setDeleting(true);
    setError('');

    try {
      // Extract public_id from Cloudinary URL
      const urlParts = imageUrl.split('/');
      const uploadIndex = urlParts.findIndex(part => part === 'upload');
      const publicId = urlParts.slice(uploadIndex + 2).join('/').split('.')[0];

      const result = await deleteImageFromCloudinary(publicId);
      
      if (result.success) {
        setSuccess('Image deleted successfully!');
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(result.error || 'Failed to delete image');
      }

    } catch (error) {
      console.error('Delete error:', error);
      setError('Failed to delete image. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const getCurrentCategoryData = () => {
    return galleryData.find(category => category.slug === selectedCategory);
  };

  const categoryOptions = getCategoryOptions();

  const testUploadConfiguration = async () => {
    console.log('Testing upload configuration...');
    const result = await testUploadPreset();
    if (result.success) {
      setSuccess('Upload preset is working correctly!');
    } else {
      setError(`Upload preset test failed: ${result.error}`);
    }
    setTimeout(() => {
      setSuccess('');
      setError('');
    }, 5000);
  };

  if (!isAdmin) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
          <p>You must be logged in as an admin to access this page.</p>
          <Button text="Go to Login" to="/login" className="mt-4" />
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Gallery Management | Admin Dashboard | Oak & Stone Construction</title>
        <meta name="description" content="Admin dashboard for managing gallery images" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Gallery Management</h1>
            <p className="text-gray-600">Upload and manage images for different gallery categories</p>
          </div>

                     {/* Category Selector */}
           <div className="mb-8 flex items-center gap-4">
             <div>
               <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                 Select Category
               </label>
               <select
                 id="category"
                 value={selectedCategory}
                 onChange={(e) => setSelectedCategory(e.target.value)}
                 className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
               >
                 {categoryOptions.map(option => (
                   <option key={option.value} value={option.value}>
                     {option.label}
                   </option>
                 ))}
               </select>
             </div>
             
             {/* Test Upload Configuration Button */}
             <div className="mt-6">
               <button
                 onClick={testUploadConfiguration}
                 className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 text-sm"
                 title="Test Cloudinary upload configuration"
               >
                 Test Upload Config
               </button>
             </div>
           </div>

                     {/* Upload Section */}
           <div className="bg-white rounded-lg shadow-md p-6 mb-8">
             <h2 className="text-xl font-semibold mb-4">Upload Images</h2>
             
             {/* Drag & Drop Area */}
             <div
               className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
                 dragActive 
                   ? 'border-blue-500 bg-blue-50' 
                   : 'border-gray-300 hover:border-gray-400'
               }`}
               onDragEnter={handleDrag}
               onDragLeave={handleDrag}
               onDragOver={handleDrag}
               onDrop={handleDrop}
             >
               <input
                 type="file"
                 accept="image/*"
                 multiple
                 onChange={handleFileSelect}
                 className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                 disabled={uploading}
               />
               
               <div className="space-y-4">
                 <FaCloudUploadAlt className="mx-auto text-4xl text-gray-400" />
                 <div>
                   <p className="text-lg font-medium text-gray-700">
                     Drop images here or click to browse
                   </p>
                   <p className="text-sm text-gray-500 mt-1">
                     Supports multiple images (max 10MB each)
                   </p>
                 </div>
               </div>
             </div>

             {/* Selected Files Preview */}
             {selectedFiles.length > 0 && (
               <div className="mt-6">
                 <h3 className="text-lg font-medium text-gray-700 mb-4">
                   Selected Files ({selectedFiles.length})
                 </h3>
                 
                 <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                   {selectedFiles.map((fileData) => (
                     <motion.div
                       key={fileData.id}
                       initial={{ opacity: 0, scale: 0.9 }}
                       animate={{ opacity: 1, scale: 1 }}
                       className="relative group"
                     >
                       <div className="relative aspect-square rounded-lg overflow-hidden border border-gray-200">
                         <img
                           src={fileData.preview}
                           alt={fileData.file.name}
                           className="w-full h-full object-cover"
                         />
                         
                         {/* Status Overlay */}
                         {fileData.status === 'uploading' && (
                           <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                             <FaSpinner className="text-white text-xl animate-spin" />
                           </div>
                         )}
                         
                         {fileData.status === 'success' && (
                           <div className="absolute inset-0 bg-green-500 bg-opacity-50 flex items-center justify-center">
                             <div className="text-white text-center">
                               <div className="text-2xl">✓</div>
                               <div className="text-xs">Success</div>
                             </div>
                           </div>
                         )}
                         
                         {fileData.status === 'error' && (
                           <div className="absolute inset-0 bg-red-500 bg-opacity-50 flex items-center justify-center">
                             <div className="text-white text-center">
                               <div className="text-2xl">✗</div>
                               <div className="text-xs">Error</div>
                             </div>
                           </div>
                         )}
                         
                         {/* Remove Button */}
                         {fileData.status === 'pending' && (
                           <button
                             onClick={() => removeFile(fileData.id)}
                             className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                             title="Remove file"
                           >
                             <FaTimes className="text-xs" />
                           </button>
                         )}
                       </div>
                       
                       {/* File Name */}
                       <p className="text-xs text-gray-600 mt-1 truncate" title={fileData.file.name}>
                         {fileData.file.name}
                       </p>
                       
                       {/* Error Message */}
                       {fileData.status === 'error' && fileData.error && (
                         <p className="text-xs text-red-600 mt-1" title={fileData.error}>
                           {fileData.error}
                         </p>
                       )}
                     </motion.div>
                   ))}
                 </div>
               </div>
             )}

             {/* Upload Button */}
             <div className="mt-6 flex items-center justify-between">
               <button
                 onClick={uploadToCloudinary}
                 disabled={selectedFiles.length === 0 || uploading}
                 className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
               >
                 {uploading ? (
                   <>
                     <FaSpinner className="animate-spin mr-2" />
                     Uploading {uploadedCount}/{selectedFiles.length}...
                   </>
                 ) : (
                   <>
                     <FaUpload className="mr-2" />
                     Upload {selectedFiles.length} Image{selectedFiles.length !== 1 ? 's' : ''}
                   </>
                 )}
               </button>
               
               {selectedFiles.length > 0 && (
                 <button
                   onClick={() => {
                     selectedFiles.forEach(f => URL.revokeObjectURL(f.preview));
                     setSelectedFiles([]);
                   }}
                   className="text-gray-500 hover:text-gray-700 text-sm"
                   disabled={uploading}
                 >
                   Clear All
                 </button>
               )}
             </div>

             {/* Progress Bar */}
             {uploading && (
               <div className="mt-4">
                 <div className="flex justify-between text-sm text-gray-600 mb-2">
                   <span>Uploading images...</span>
                   <span>{uploadedCount} / {selectedFiles.length}</span>
                 </div>
                 <div className="w-full bg-gray-200 rounded-full h-2">
                   <div
                     className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                     style={{ width: `${uploadProgress}%` }}
                   ></div>
                 </div>
               </div>
             )}
           </div>

          {/* Messages */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md"
              >
                {error}
              </motion.div>
            )}
            {success && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-md"
              >
                {success}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Current Images */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">
              Current Images - {getCurrentCategoryData()?.title}
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {getCurrentCategoryData()?.imageCollection.map((image, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="relative group"
                >
                                     <img
                     src={getImageUrl(image, 'medium')}
                     alt={`Gallery ${index + 1}`}
                     className="w-full h-48 object-cover rounded-lg"
                   />
                  
                  {/* Overlay with actions */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 rounded-lg flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-2">
                      <button
                        onClick={() => window.open(image, '_blank')}
                        className="p-2 bg-white rounded-full hover:bg-gray-100"
                        title="View full size"
                      >
                        <FaEye className="text-gray-700" />
                      </button>
                      <button
                        onClick={() => deleteImage(image, index)}
                        disabled={deleting}
                        className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 disabled:opacity-50"
                        title="Delete image"
                      >
                        {deleting ? (
                          <FaSpinner className="animate-spin" />
                        ) : (
                          <FaTrash />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  {/* Image number */}
                  <div className="absolute top-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                    #{index + 1}
                  </div>
                </motion.div>
              ))}
            </div>

            {getCurrentCategoryData()?.imageCollection.length === 0 && (
              <p className="text-gray-500 text-center py-8">
                No images in this category yet.
              </p>
            )}
          </div>

          {/* Navigation */}
          <div className="mt-8 flex justify-between">
            <Button text="Back to Dashboard" to="/admin" />
            <Button text="View Gallery" to="/gallery" />
          </div>
        </div>
      </div>
    </>
  );
};

export default GalleryManagement;
